import React, { Component } from "react";
import { Link, withRouter } from "react-router-dom";
import axios from "axios";
import { Store } from "react-notifications-component";
import {
  Mail, Send, Users, CheckCircle2, XCircle, Eye, Ban, Trash2,
} from "lucide-react";

import AdminPageShell from "./AdminPageShell";
import setAuthToken from "../../utils/setAuthToken";
import confirmAlert from "../../utils/confirmAlert";

import {
  PageHeader,
  Button,
  IconButton,
  Tooltip,
  Badge,
  DataTable,
  Stat,
  Select,
  FormField,
  Modal,
  Input,
  SegmentedControl,
  StatusIndicator,
} from "../../design-system";

class NewsletterList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      subscribers: [],
      stats: { total: 0, active: 0, inactive: 0 },
      loading: true,
      filterActive: "",
      showTestModal: false,
      testEmail: "",
      testArticleId: "",
      articles: [],
      loadingArticles: false,
      testing: false,
    };
  }

  componentDidMount() {
    this.loadSubscribers();
  }

  loadSubscribers = async () => {
    try {
      const token = localStorage.getItem("jwtToken");
      if (token) setAuthToken(token);
      const params = new URLSearchParams();
      if (this.state.filterActive !== "") params.append("active", this.state.filterActive);
      const response = await axios.get(`/api/newsletters/subscribers?${params.toString()}`);
      this.setState({
        subscribers: response.data.subscribers || [],
        stats: response.data.stats || { total: 0, active: 0, inactive: 0 },
        loading: false,
      });
    } catch (error) {
      console.error("Error loading subscribers:", error);
      this.setState({ loading: false });
      Store.addNotification({
        title: "Erreur", message: "Impossible de charger les abonnés",
        type: "danger", insert: "top", container: "top-right", dismiss: { duration: 3000 },
      });
    }
  };

  loadArticles = async () => {
    try {
      this.setState({ loadingArticles: true });
      const token = localStorage.getItem("jwtToken");
      if (token) setAuthToken(token);
      const response = await axios.get("/api/articles?limit=100&status=PUBLISHED");
      if (response.data.success) {
        this.setState({ articles: response.data.articles || [], loadingArticles: false });
      }
    } catch (error) {
      console.error("Error loading articles:", error);
      this.setState({ loadingArticles: false });
    }
  };

  handleOpenTestModal = () => {
    this.setState({ showTestModal: true });
    if (this.state.articles.length === 0) this.loadArticles();
  };

  handleCloseTestModal = () => {
    this.setState({ showTestModal: false, testEmail: "", testArticleId: "", testing: false });
  };

  handleTestNewsletter = async () => {
    const { testEmail, testArticleId } = this.state;
    if (!testEmail || !testEmail.includes("@")) {
      Store.addNotification({
        title: "Erreur", message: "Adresse email invalide",
        type: "danger", insert: "top", container: "top-right", dismiss: { duration: 3000 },
      });
      return;
    }
    try {
      this.setState({ testing: true });
      const token = localStorage.getItem("jwtToken");
      if (token) setAuthToken(token);
      const payload = { testEmail, ...(testArticleId && { articleId: testArticleId }) };
      const response = await axios.post("/api/newsletters/test", payload);
      if (response.data.success) {
        Store.addNotification({
          title: "Newsletter envoyée",
          message: response.data.message || "Test envoyé.",
          type: "success", insert: "top", container: "top-right", dismiss: { duration: 5000 },
        });
        this.handleCloseTestModal();
      }
    } catch (error) {
      console.error("Error testing newsletter:", error);
      Store.addNotification({
        title: "Erreur",
        message: error.response?.data?.message || "Impossible d'envoyer le test",
        type: "danger", insert: "top", container: "top-right", dismiss: { duration: 5000 },
      });
    } finally {
      this.setState({ testing: false });
    }
  };

  handleFilterChange = (e) => {
    this.setState({ filterActive: e.target.value }, this.loadSubscribers);
  };

  handleUnsubscribe = (subscriber) => {
    confirmAlert({
      title: "Désabonner",
      message: `Désabonner « ${subscriber.email} » ?`,
      type: "warning",
      confirmText: "Désabonner",
      cancelText: "Annuler",
      onConfirm: async () => {
        try {
          const token = localStorage.getItem("jwtToken");
          if (token) setAuthToken(token);
          await axios.put(`/api/newsletters/subscribers/${subscriber.id}/unsubscribe`);
          Store.addNotification({
            title: "Désabonné",
            type: "success", insert: "top", container: "top-right", dismiss: { duration: 3000 },
          });
          this.loadSubscribers();
        } catch (error) {
          console.error("Error unsubscribing:", error);
          Store.addNotification({
            title: "Erreur",
            message: error.response?.data?.message || "Impossible de désabonner",
            type: "danger", insert: "top", container: "top-right", dismiss: { duration: 3000 },
          });
        }
      },
    });
  };

  handleDelete = (subscriber) => {
    confirmAlert({
      title: "Supprimer l'abonné",
      message: `« ${subscriber.email} » sera supprimé définitivement.`,
      type: "danger",
      confirmText: "Supprimer",
      cancelText: "Annuler",
      onConfirm: async () => {
        try {
          const token = localStorage.getItem("jwtToken");
          if (token) setAuthToken(token);
          await axios.delete(`/api/newsletters/subscribers/${subscriber.id}`);
          Store.addNotification({
            title: "Abonné supprimé",
            type: "success", insert: "top", container: "top-right", dismiss: { duration: 3000 },
          });
          this.loadSubscribers();
        } catch (error) {
          console.error("Error deleting subscriber:", error);
          Store.addNotification({
            title: "Erreur",
            message: error.response?.data?.message || "Impossible de supprimer",
            type: "danger", insert: "top", container: "top-right", dismiss: { duration: 3000 },
          });
        }
      },
    });
  };

  buildColumns() {
    return [
      {
        key: "email", header: "Email", sortable: true,
        render: (row) => <span className="font-semibold text-text">{row.email}</span>,
      },
      {
        key: "name", header: "Nom", width: 180,
        render: (row) => <span className="text-sm text-text-2">{row.name || "—"}</span>,
      },
      {
        key: "active", header: "Statut", width: 130,
        render: (row) => row.active
          ? <StatusIndicator tone="ink">Actif</StatusIndicator>
          : <StatusIndicator tone="muted">Désabonné</StatusIndicator>,
      },
      {
        key: "subscribedAt", header: "Abonné le", sortable: true, width: 140,
        render: (row) => (
          <span className="font-mono text-xs text-mute">
            {row.subscribedAt ? new Date(row.subscribedAt).toLocaleDateString("fr-FR", {
              year: "numeric", month: "short", day: "numeric",
            }) : "—"}
          </span>
        ),
      },
      {
        key: "unsubscribedAt", header: "Désabonné le", width: 140,
        render: (row) => (
          <span className="font-mono text-xs text-mute">
            {row.unsubscribedAt ? new Date(row.unsubscribedAt).toLocaleDateString("fr-FR", {
              year: "numeric", month: "short", day: "numeric",
            }) : "—"}
          </span>
        ),
      },
      {
        key: "actions", header: "", width: 130,
        render: (row) => (
          <div className="flex items-center gap-0.5 justify-end" data-no-row-click>
            <Tooltip content="Détails">
              <IconButton as={Link} to={`/dashboard/newsletter/${row.id}`}
                size="sm" variant="ghost" icon={<Eye strokeWidth={1.8} />}
                aria-label={`Voir ${row.email}`} />
            </Tooltip>
            {row.active && (
              <Tooltip content="Désabonner">
                <IconButton size="sm" variant="ghost" icon={<Ban strokeWidth={1.8} />}
                  aria-label={`Désabonner ${row.email}`}
                  onClick={() => this.handleUnsubscribe(row)} />
              </Tooltip>
            )}
            <Tooltip content="Supprimer">
              <IconButton size="sm" variant="ghost" icon={<Trash2 strokeWidth={1.8} />}
                aria-label={`Supprimer ${row.email}`}
                onClick={() => this.handleDelete(row)} />
            </Tooltip>
          </div>
        ),
      },
    ];
  }

  render() {
    const { subscribers, stats, loading, filterActive, showTestModal, testing,
      testEmail, testArticleId, articles, loadingArticles } = this.state;

    return (
      <AdminPageShell pageTitle="Newsletter">
        <div className="space-y-6 lg:space-y-7">
        <PageHeader
          size="hero"
          eyebrow="— Section v · Newsletter"
          title={<>L'envoi qui part <span className="font-serif italic font-normal align-baseline">le jeudi.</span></>}
          description="Tous les abonnés à la newsletter du portfolio. Teste un envoi avant de publier."
          actions={
            <Button
              leftIcon={<Send className="w-4 h-4" strokeWidth={1.8} />}
              onClick={this.handleOpenTestModal}
            >
              Tester l'envoi
            </Button>
          }
        />

        <Stat.Group>
          <Stat variant="ink"   label="Total"      value={String(stats.total)}    icon={<Users className="w-4 h-4" strokeWidth={1.8} />} />
          <Stat variant="cream" label="Actifs"     value={String(stats.active)}   icon={<CheckCircle2 className="w-4 h-4" strokeWidth={1.8} />} />
          <Stat variant="cream" label="Désabonnés" value={String(stats.inactive)} icon={<XCircle className="w-4 h-4" strokeWidth={1.8} />} />
          <Stat variant="ink"   label="Taux actif" value={stats.total ? `${Math.round((stats.active / stats.total) * 100)} %` : '—'} ghost={stats.total ? `${Math.round((stats.active / stats.total) * 100)}%` : null} hint="abonnés actifs / total" />
        </Stat.Group>

        <div className="flex items-center justify-between gap-3 flex-wrap">
          <SegmentedControl
            value={filterActive || "ALL"}
            onChange={(v) => this.setState({ filterActive: v === "ALL" ? "" : v }, this.loadSubscribers)}
            options={[
              { value: "ALL",   label: "Tous",       count: stats.total },
              { value: "true",  label: "Actifs",     count: stats.active },
              { value: "false", label: "Désabonnés", count: stats.inactive },
            ]}
            ariaLabel="Filtrer par statut"
          />
          <div className="text-xs text-mute font-mono">{subscribers.length} affiché(s)</div>
        </div>

        <DataTable
          data={subscribers}
          columns={this.buildColumns()}
          rowKey="id"
          isLoading={loading}
          variant="duotone"
          showRowIndex
        />
        </div>

        {/* Modal de test */}
        <Modal
          isOpen={showTestModal}
          onClose={this.handleCloseTestModal}
          size="md"
          title="Tester l'envoi de newsletter"
          description="Envoie une copie de test à une adresse email."
          footer={
            <>
              <Button variant="secondary" onClick={this.handleCloseTestModal} isDisabled={testing}>
                Annuler
              </Button>
              <Button
                onClick={this.handleTestNewsletter}
                isLoading={testing}
                isDisabled={!testEmail}
                leftIcon={<Mail className="w-4 h-4" strokeWidth={1.8} />}
              >
                Envoyer le test
              </Button>
            </>
          }
        >
          <div className="space-y-4">
            <FormField
              label="Email de test"
              required
              helper="L'email sera ajouté à la liste s'il n'existe pas déjà."
            >
              <Input
                type="email"
                value={testEmail}
                onChange={(e) => this.setState({ testEmail: e.target.value })}
                placeholder="vous@exemple.fr"
                autoFocus
              />
            </FormField>
            <FormField
              label="Article (optionnel)"
              helper="Si vide, un contenu de test sera utilisé."
            >
              <Select
                value={testArticleId}
                onChange={(e) => this.setState({ testArticleId: e.target.value })}
                placeholder={loadingArticles ? "Chargement…" : "Utiliser un article de test"}
                options={articles.map((a) => ({ value: a.id, label: a.title }))}
                isDisabled={loadingArticles}
              />
            </FormField>
          </div>
        </Modal>
      </AdminPageShell>
    );
  }
}

export default withRouter(NewsletterList);
